import { generateCorrelationId, trackError } from '@gs/telemetry';
import { getRegisteredAccessToken } from './auth';
import { ApiUnauthorizedError, ApiNetworkError, errorFromResponse } from './errors';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * Construit les query params à partir d'un objet.
 * IMPORTANT : les arrays sont joints par virgule (compat Zod côté serveur via split(',')).
 */
export function buildQueryString(params?: Record<string, unknown>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)
  );
  if (entries.length === 0) return '';
  const qs = new URLSearchParams();
  for (const [key, value] of entries) {
    if (Array.isArray(value)) {
      qs.set(key, value.map(String).join(','));
    } else {
      qs.set(key, String(value));
    }
  }
  return `?${qs.toString()}`;
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  params?: Record<string, unknown>;
  body?: unknown;
  /** Interne — utilisé pour empêcher la boucle infinie sur retry */
  _isRetry?: boolean;
}

/**
 * Wrapper fetch avec auth Bearer automatique + retry 401 automatique.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const getToken = getRegisteredAccessToken();
  if (!getToken) {
    throw new ApiUnauthorizedError('Non authentifié — getAccessToken non enregistré');
  }

  const { params, body, _isRetry, ...fetchOptions } = options;
  const queryString = buildQueryString(params);
  const serializedBody = body === undefined ? undefined : JSON.stringify(body);

  let token: string;
  try {
    token = await getToken({ forceRefresh: Boolean(_isRetry) });
  } catch (err) {
    throw new ApiUnauthorizedError(
      err instanceof Error ? err.message : 'Impossible d\'obtenir un jeton'
    );
  }

  const correlationId = generateCorrelationId();

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}${queryString}`, {
      ...fetchOptions,
      body: serializedBody,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'x-request-id': correlationId,
        ...fetchOptions.headers,
      },
    });
  } catch (err) {
    const netErr = new ApiNetworkError(
      err instanceof Error ? err.message : 'Erreur réseau inconnue',
      err
    );
    trackError(netErr, { path, correlationId });
    throw netErr;
  }

  if (res.status === 401 && !_isRetry) {
    return apiFetch<T>(path, { ...options, _isRetry: true });
  }

  if (!res.ok) {
    const apiErr = await errorFromResponse(res);
    trackError(apiErr, { path, correlationId, status: res.status });
    throw apiErr;
  }

  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>) => apiFetch<T>(path, { params }),
  post: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
